import { type Permission, hasPermission } from "@pokifolio/contracts";
import {
  Bookmark,
  CheckCircle,
  ClipboardList,
  LayoutDashboard,
  Library,
  List,
  type LucideIcon,
  Search,
  Users,
} from "lucide-react";

export interface NavItem {
  readonly title: string;
  readonly to:
    "/" | "/cards" | "/listings" | "/search" | "/research" | "/approvals" | "/portfolio" | "/users";
  readonly icon: LucideIcon;
  /** Shown only to people with this permission; none means everyone signed in. */
  readonly permission?: Permission;
  /** Words that also find this page in the quick search (Ctrl+K). */
  readonly keywords?: readonly string[];
}

export interface NavGroup {
  readonly title: string;
  readonly items: readonly NavItem[];
}

/**
 * The menu. A new screen (for example a team chat) is one more item here plus its route;
 * the sidebar and the quick search pick it up from this list.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    title: "General",
    items: [
      { title: "Dashboard", to: "/", icon: LayoutDashboard, keywords: ["home", "overview"] },
      {
        title: "Research",
        to: "/cards",
        icon: ClipboardList,
        permission: "research.create",
        keywords: ["cards", "add card"],
      },
      {
        title: "Listings",
        to: "/listings",
        icon: List,
        permission: "research.create",
        keywords: ["links", "approved", "declined", "condition", "filter"],
      },
      { title: "Search", to: "/search", icon: Search, permission: "search.run", keywords: ["find", "ebay"] },
      {
        title: "Saved from search",
        to: "/research",
        icon: Bookmark,
        permission: "search.run",
        keywords: ["saved", "condition", "price"],
      },
      {
        title: "Portfolio",
        to: "/portfolio",
        icon: Library,
        permission: "holdings.view",
        keywords: ["owned", "collection", "photos"],
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        title: "Approvals",
        to: "/approvals",
        icon: CheckCircle,
        permission: "research.approve",
        keywords: ["approve", "reject", "requests"],
      },
      {
        title: "Users",
        to: "/users",
        icon: Users,
        permission: "users.manage",
        keywords: ["people", "accounts"],
      },
    ],
  },
];

/** The groups and items this person may open; empty groups are left out. */
export function visibleNav(permissions: readonly Permission[]): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => item.permission === undefined || hasPermission(permissions, item.permission),
    ),
  })).filter((group) => group.items.length > 0);
}
